/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC, VFC } from 'react';
import React, { useContext, useState, useMemo, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { styled } from '@compiled/react';

import Spinner from '@atlaskit/spinner/spinner';
import { FadeIn } from '@atlaskit/motion';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ExperienceTrackerContext } from '@confluence/experience-tracker';
import { useChoreographerAssets } from '@confluence/choreographer-services';

import type { SpaceAccessDetailsParamsType } from '../constants';

import type {
	AccessDetailsDialogContentAccessDetailsQuery as AccessDetailsDialogContentAccessDetailsQuery$data,
	AccessDetailsDialogContentAccessDetailsQueryVariables as AccessDetailsDialogContentAccessDetailsQuery$variables,
} from './__types__/AccessDetailsDialogContentAccessDetailsQuery';
import type {
	AccessDetailsDialogSpaceAccessDetailsQuery as AccessDetailsDialogSpaceAccessDetailsQuery$data,
	AccessDetailsDialogSpaceAccessDetailsQueryVariables as AccessDetailsDialogSpaceAccessDetailsQuery$variables,
} from './__types__/AccessDetailsDialogSpaceAccessDetailsQuery';
import { AccessDetailsDialogHeader } from './AccessDetailsDialogHeader';
import { AccessDetailsDialogBody } from './AccessDetailsDialogBody';

const AccessDetailsDialogSpaceAccessDetailsQuery = gql`
	query AccessDetailsDialogSpaceAccessDetailsQuery(
		$spaceKey: String!
		$first: Int
		$after: String
		$filterText: String
	) {
		space(key: $spaceKey) {
			id
			key
			name
			icon {
				path(type: ABSOLUTE)
				isDefault
			}
			type
		}
		spacePermissions(spaceKey: $spaceKey) {
			subjectsWithPermissions(first: $first, after: $after, filterText: $filterText) {
				groupCount
				userCount
				nodes {
					permissions
					subjectKey {
						id
						displayName
						principalType: confluencePrincipalType
						user {
							accountId
							displayName
							email
							permissionType
							profilePicture {
								width
								height
								isDefault
								path(type: ABSOLUTE)
							}
						}
						group {
							id
							name
							permissionType
						}
					}
				}
				pageInfo {
					hasNextPage
					endCursor
				}
			}
		}
	}
`;
const AccessDetailsDialogContentAccessDetailsQuery = gql`
	query AccessDetailsDialogContentAccessDetailsQuery(
		$contentId: ID!
		$first: Int
		$after: String
		$filterText: String
	) {
		contentPermissions(contentId: $contentId) {
			subjectsWithEffectiveContentPermissions(
				first: $first
				after: $after
				filterText: $filterText
			) {
				groupCount
				userCount
				nodes {
					id
					permissions
					type
					displayName
					group {
						id
						name
						permissionType
					}
					user {
						accountId
						displayName
						email
						permissionType
						profilePicture {
							width
							height
							isDefault
							path(type: ABSOLUTE)
						}
					}
				}
				pageInfo {
					hasNextPage
					endCursor
				}
			}
		}
		singleContent(id: $contentId) {
			id
			title
			type
			links {
				webui
			}
		}
	}
`;
export type AccessDetailsDialogProps = {
	onClose: () => void;
	accessDetailsParams: SpaceAccessDetailsParamsType;
};

type FetchMoreSpaceResultType = {
	fetchMoreResult?: AccessDetailsDialogSpaceAccessDetailsQuery$data;
};

type FetchMoreContentResultType = {
	fetchMoreResult?: AccessDetailsDialogContentAccessDetailsQuery$data;
};

const PAGE_SIZE = 20;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingSpinnerContainer = styled.div({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'center',
	alignItems: 'center',
	height: '220.97px',
});

const LoadingSpinner: VFC = () => (
	<LoadingSpinnerContainer>
		<Spinner size="medium" />
	</LoadingSpinnerContainer>
);

function mergeFetchMoreSpaceResults(
	prev: AccessDetailsDialogSpaceAccessDetailsQuery$data,
	{ fetchMoreResult }: FetchMoreSpaceResultType,
): AccessDetailsDialogSpaceAccessDetailsQuery$data {
	const nodes = [
		...(prev?.spacePermissions?.subjectsWithPermissions?.nodes || []),
		...(fetchMoreResult?.spacePermissions?.subjectsWithPermissions?.nodes || []),
	];
	const pageInfo = fetchMoreResult?.spacePermissions?.subjectsWithPermissions?.pageInfo || null;

	return {
		...prev,
		spacePermissions: {
			...prev.spacePermissions,
			subjectsWithPermissions: {
				...prev.spacePermissions.subjectsWithPermissions,
				nodes,
				pageInfo,
			},
		},
	};
}

function mergeFetchMoreContentResults(
	prev: AccessDetailsDialogContentAccessDetailsQuery$data,
	{ fetchMoreResult }: FetchMoreContentResultType,
): AccessDetailsDialogContentAccessDetailsQuery$data {
	const nodes = [
		...(prev?.contentPermissions?.subjectsWithEffectiveContentPermissions?.nodes || []),
		...(fetchMoreResult?.contentPermissions?.subjectsWithEffectiveContentPermissions?.nodes || []),
	];
	const pageInfo =
		fetchMoreResult?.contentPermissions?.subjectsWithEffectiveContentPermissions?.pageInfo || null;

	const userCount =
		fetchMoreResult?.contentPermissions?.subjectsWithEffectiveContentPermissions?.userCount || 0;

	const groupCount =
		fetchMoreResult?.contentPermissions?.subjectsWithEffectiveContentPermissions?.groupCount || 0;

	return {
		...prev,
		contentPermissions: {
			...prev?.contentPermissions,
			subjectsWithEffectiveContentPermissions: {
				...prev?.contentPermissions?.subjectsWithEffectiveContentPermissions,
				pageInfo,
				nodes,
				userCount,
				groupCount,
			},
		},
	};
}

export const AccessDetailsDialog: FC<AccessDetailsDialogProps> = ({
	onClose,
	accessDetailsParams,
}) => {
	const [filterText, setFilterText] = useState('');
	const [appliedFilterText, setAppliedFilterText] = useState('');
	const debouncedSetFilterText = useMemo(() => debounce(setAppliedFilterText, 200), []);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { Modal } = useChoreographerAssets();
	useEffect(() => {
		debouncedSetFilterText(filterText);
	}, [filterText, debouncedSetFilterText]);

	const { spaceKey, restrictingContentId, hasInheritedRestrictions, experience } =
		accessDetailsParams;

	const {
		data: spaceAccessDetails,
		loading: spaceAccessDetailsLoading,
		error: spaceAccessDetailsError,
		fetchMore: spaceAccessDetailsFetchMore,
	} = useQuery<
		AccessDetailsDialogSpaceAccessDetailsQuery$data,
		AccessDetailsDialogSpaceAccessDetailsQuery$variables
	>(AccessDetailsDialogSpaceAccessDetailsQuery, {
		variables: {
			spaceKey,
			first: PAGE_SIZE,
			filterText: appliedFilterText,
		},
		skip: hasInheritedRestrictions,
	});
	const handleSpaceAccessDetailsFetchMore = useCallback(
		(nextPageCursor: any) => {
			void spaceAccessDetailsFetchMore({
				variables: {
					after: nextPageCursor,
				},
				updateQuery: mergeFetchMoreSpaceResults,
			});
		},
		[spaceAccessDetailsFetchMore],
	);

	const {
		data: contentAccessDetails,
		loading: contentAccessDetailsLoading,
		error: contentAccessDetailsError,
		fetchMore: contentAccessDetailsFetchMore,
	} = useQuery<
		AccessDetailsDialogContentAccessDetailsQuery$data,
		AccessDetailsDialogContentAccessDetailsQuery$variables
	>(AccessDetailsDialogContentAccessDetailsQuery, {
		variables: {
			contentId: restrictingContentId ?? '',
			filterText: appliedFilterText,
			first: PAGE_SIZE,
		},
		skip: !hasInheritedRestrictions,
	});
	const handleContentAccessDetailsFetchMore = useCallback(
		(nextPageCursor: any) => {
			void contentAccessDetailsFetchMore({
				variables: {
					after: nextPageCursor,
				},
				updateQuery: mergeFetchMoreContentResults,
			});
		},
		[contentAccessDetailsFetchMore],
	);

	if (spaceAccessDetailsError || contentAccessDetailsError) {
		if (spaceAccessDetailsError) {
			experienceTracker.fail({
				name: experience,
				error: spaceAccessDetailsError,
			});
			throw spaceAccessDetailsError;
		}
		if (contentAccessDetailsError) {
			experienceTracker.fail({
				name: experience,
				error: contentAccessDetailsError,
			});
			throw contentAccessDetailsError;
		}
	}

	const isLoading = contentAccessDetailsLoading || spaceAccessDetailsLoading;

	// Don't render spinner when search bar entires are querying results
	const [initialLoadComplete, setInitialLoadComplete] = useState(false);
	useEffect(() => {
		if (!contentAccessDetailsLoading && !spaceAccessDetailsLoading) {
			if (!contentAccessDetailsError && !spaceAccessDetailsError) {
				setInitialLoadComplete(true);

				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						source: experience,
						actionSubjectId: 'ViewSpaceAccessModal',
						action: 'viewed',
					},
				}).fire();
			}
		}
	}, [
		contentAccessDetailsLoading,
		spaceAccessDetailsLoading,
		contentAccessDetailsError,
		spaceAccessDetailsError,
		createAnalyticsEvent,
		experience,
	]);

	//TODO: The body should likely be split into two components, one for content access and one for space access,
	//      either could be passed to a body wrapper component.  That way, there aren't multiple query's data in the same file.
	return (
		<Modal onClose={onClose} testId="space-access-details.drilldown">
			{isLoading && !initialLoadComplete ? (
				<LoadingSpinner />
			) : (
				<FadeIn>
					{(props) => (
						<>
							<AccessDetailsDialogHeader
								filterText={filterText}
								appliedFilterText={appliedFilterText}
								setFilterText={setFilterText}
								onClose={onClose}
								hasInheritedRestrictions={hasInheritedRestrictions}
								spaceInfo={spaceAccessDetails}
								contentInfo={contentAccessDetails}
								isLoading={isLoading}
								{...props}
							/>
							<AccessDetailsDialogBody
								spaceInfo={spaceAccessDetails}
								contentInfo={contentAccessDetails}
								hasInheritedRestrictions={hasInheritedRestrictions}
								handleSpaceAccessDetailsFetchMore={handleSpaceAccessDetailsFetchMore}
								handleContentAccessDetailsFetchMore={handleContentAccessDetailsFetchMore}
								isLoading={isLoading}
								{...props}
							/>
						</>
					)}
				</FadeIn>
			)}
		</Modal>
	);
};
